import { ResponsibleParty } from '../../insights/types/RoleTypes';
import { ApplicationGroup } from '../../insights/types/InsightsTypes';
import { InformationClassificationPolicy } from '../../insights/types/PolicyTypes';
import { BusinessCapability } from '../../common/BusinessCapabilitiesSelect';
import { ServiceOffering } from '../../common/ServiceOfferingSelect';
import { ServiceBusiness } from '../../common/ServiceBusinessSelect';
import comcast from '../../../app/icons/entityLogo/comcast.svg';
import nbcUniversal from '../../../app/icons/entityLogo/nbcu.svg';
import sky from '../../../app/icons/entityLogo/sky.svg';
import { DocumentNode, gql } from '@apollo/client';

export interface AppDetails {
  entity: string;
  [key: string]: string | number | undefined | ResponsibleParty;
  name: string;
  description: string;
  status: string;
  serviceLevelTier: number;
  owner: ResponsibleParty;
  consumerType: string;
  systemType: string;
  serviceDomain?: number;
  securityPoc?: ResponsibleParty;
  url?: string;
}

export interface AdditionalDetails {
  entity: string;
  [key: string]:
    | string
    | undefined
    | ResponsibleParty
    | ApplicationGroup
    | InformationClassificationPolicy
    | BusinessCapability[]
    | ServiceOffering
    | ServiceBusiness
    | null;
  securityPoc?: ResponsibleParty;
  iopOwner: string;
  iopGroup: string;
  appGroup: ApplicationGroup;
  infoClass?: InformationClassificationPolicy;
  businessCapabilities?: BusinessCapability[];
  serviceOffering?: ServiceOffering | null;
  serviceBusiness?: ServiceBusiness | null;
}

export type SurveyItem = {
  text: string;
};

export type Survey = {
  CPP_CERT2_7: boolean;
  CPP_CERT2_8: boolean;
  CPP_CERT2_9: boolean;
  CPP_CERT2_10: boolean;
  CPP_CERT2_11: boolean;
  CPP_CERT2_12: boolean;
  CPP_CERT2_13: boolean;
};

export type PrivacyQuestion = {
  question: string;
  answer: boolean;
};

export type PrivacySurvey = {
  responses: PrivacyQuestion[];
};

export enum CorporateEntity {
  /** Applications belonging to Cable */
  CABLE = 'Cable',
  /** Application belonging to NBCU */
  NBCU = 'Nbcu',
  /** Application belonging to Sky */
  SKY = 'Sky',
}

export type EntityProp = {
  name: CorporateEntity;
  domains: string[];
  svg: string;
  displayName: string;
  fusionQuery: DocumentNode;
  appGroupId?: number;
};

const createCableApplication = gql`
  mutation CreateApplication($input: CreateCableApplicationInput!) {
    resources {
      application {
        createApplication: createCableApplication(input: $input) {
          success
          applicationId
        }
      }
    }
  }
`;

const createNbcuApplication = gql`
  mutation createNbcuApplication($input: CreateNbcuApplicationInput!) {
    resources {
      application {
        createApplication: createNbcuApplication(input: $input) {
          success
          applicationId
        }
      }
    }
  }
`;

const createSkyApplication = gql`
  mutation createSkyApplication($input: CreateSkyApplicationInput!) {
    resources {
      application {
        createApplication: createSkyApplication(input: $input) {
          success
          applicationId
        }
      }
    }
  }
`;

/**
 * Allowed email extensions for different corporate entities.
 */
export const allowedExtensions: Record<CorporateEntity, string[]> = {
  [CorporateEntity.CABLE]: ['comcast.com', 'cable.comcast.com', 'masergy.com'],
  [CorporateEntity.SKY]: ['sky.uk', 'sky.de', 'skytv.it'],
  [CorporateEntity.NBCU]: ['nbcuni.com'],
};

/**
 * Application Group enum
 */
export enum ApplicationGroupEnum {
  NBCU = 111491,
  SKY = 111484,
}

/**
 * Corporate entity properties
 */
export const CorporateEntityProps: EntityProp[] = [
  {
    name: CorporateEntity.CABLE,
    domains: allowedExtensions[CorporateEntity.CABLE],
    svg: comcast,
    displayName: 'Comcast Cable',
    fusionQuery: createCableApplication,
  },
  {
    name: CorporateEntity.NBCU,
    domains: allowedExtensions[CorporateEntity.NBCU],
    svg: nbcUniversal,
    displayName: 'NBCUniversal',
    fusionQuery: createNbcuApplication,
    appGroupId: ApplicationGroupEnum.NBCU,
  },
  {
    name: CorporateEntity.SKY,
    domains: allowedExtensions[CorporateEntity.SKY],
    svg: sky,
    displayName: 'Sky',
    fusionQuery: createSkyApplication,
    appGroupId: ApplicationGroupEnum.SKY,
  },
];

/**
 * Get the corporate entity from an email address.
 * @param email The email address to get the corporate entity from.
 */
export const getCorporateEntityFromEmail = (email: string) => {
  const domain = email?.split('@')[1]?.toLowerCase() || '';
  return CorporateEntityProps.find(entity => entity.domains.includes(domain))
    ?.name;
};

/**
 * AD Group Details interface for audit events
 */
export interface ADGroupDetails {
  groupId: string;
  groupName: string;
  groupType: string;
  applicationId: number;
  applicationName: string;
  message: string;
}

/**
 * Audit Event interface
 */
export interface AuditEvent {
  identity: string;
  timestamp: string;
  details: ADGroupDetails;
  eventSubject: string;
  eventType: string;
}

/**
 * GraphQL query to fetch AD group audit events
 */
export const GetADGroupsQuery = gql`
  query Resources($applicationId: Int!) {
    resources {
      application(applicationId: $applicationId) {
        resources {
          adGroupsPaged {
            totalCount
            adGroups {
              groupName
              groupId
            }
          }
        }
      }
    }
  }
`;

/**
 * Event types for AD Group creation tracking
 */
export const AD_GROUP_EVENT_TYPES = {
  CREATED: 'ADGroupCreated',
  PARTIAL_FAILURE: 'ADGroupCreationPartialFailure',
  FAILED: 'ADGroupCreationFailed',
} as const;
